const FINCART_BASE_URL = "https://api.fincart.io";
const FINCART_ACCESS_TOKEN =
  "48928b0af412461c1926cedcf062d233:97c323c46a558a85a05ea9c6a00d30bca29dec97b58096ee:25ddbd702c23068dc033e6c6469b3963";

const fincartHeaders = {
  "Content-Type": "application/json",
  Authorization: FINCART_ACCESS_TOKEN,
};

export const normalizeArabicName = (value = "") =>
  value
    .replace(/ى/g, "ي")
    .replace(/\s+/g, " ")
    .trim();

export const fetchFincartGovernorates = async () => {
  const response = await fetch(`${FINCART_BASE_URL}/merchant/config/gov`, {
    method: "GET",
    headers: fincartHeaders,
  });

  if (!response.ok) {
    throw new Error("فشل تحميل المحافظات من Fincart");
  }

  const data = await response.json();
  const governorates = data?.body?.governorates;
  if (!Array.isArray(governorates)) {
    throw new Error("استجابة Fincart غير صالحة (المحافظات)");
  }
  return governorates;
};

export const fetchFincartPickupLocations = async () => {
  const response = await fetch(`${FINCART_BASE_URL}/merchant/locations`, {
    method: "GET",
    headers: fincartHeaders,
  });

  if (!response.ok) {
    throw new Error("فشل تحميل مواقع الاستلام من Fincart");
  }

  const data = await response.json();
  const locations = data?.body;
  if (!Array.isArray(locations) || locations.length === 0) {
    throw new Error("لا يوجد موقع استلام في حساب Fincart");
  }
  return locations;
};

export const getDefaultPickupLocationId = async () => {
  const locations = await fetchFincartPickupLocations();
  const defaultLocation = locations.find((loc) => loc.is_default) || locations[0];
  return defaultLocation._id;
};

/**
 * Map our Arabic state/city to Fincart English city + area names.
 * order.state = governorate (Arabic), order.city = area (Arabic label from checkout).
 */
export const resolveFincartCityArea = (order, governorates) => {
  const stateAr = normalizeArabicName(order.state);
  const areaAr = normalizeArabicName(order.city);

  if (!stateAr) {
    throw new Error("المحافظة غير موجودة");
  }
  if (!areaAr) {
    throw new Error("المدينة/المنطقة غير موجودة — لازم تكون مختارة من قائمة Fincart");
  }

  const gov = governorates.find(
    (g) => normalizeArabicName(g.name_ar) === stateAr
  );
  if (!gov) {
    throw new Error(`المحافظة "${order.state}" غير موجودة في Fincart`);
  }

  const area = (gov.areas || []).find((a) => {
    const candidates = [a.label_ar, a.name_ar, a.label, a.label_en, a.name_en];
    return candidates.some((c) => normalizeArabicName(c) === areaAr);
  });

  if (!area) {
    throw new Error(
      `المنطقة "${order.city}" غير مطابقة لقائمة Fincart في محافظة ${order.state}`
    );
  }

  return {
    city: gov.name,
    area: area.name_en || area.label || area.label_en,
  };
};

const buildDescription = (order, note = "") => {
  let description = "Girabat order";

  if (order.items?.length) {
    const counts = {};
    order.items.forEach((item) => {
      const key = item.type || item.name || "Item";
      counts[key] = (counts[key] || 0) + 1;
    });

    description = Object.entries(counts)
      .map(([name, qty]) => `${qty} X ${name}`)
      .join(", ");
  }

  const trimmedNote = String(note || "").trim();
  if (trimmedNote) {
    description = `${description} | ${trimmedNote}`;
  }
  return description;
};

/** Allowed payment types per order type (Fincart API). */
export const FINCART_PAYMENT_OPTIONS = {
  delivery: [
    { value: "with_cash_collection", label: "مع تحصيل كاش", requiresAmount: true },
    { value: "without_cash_collection", label: "بدون تحصيل كاش", requiresAmount: false },
  ],
  exchange: [
    { value: "with_cash_collection", label: "مع تحصيل كاش (فرق)", requiresAmount: true },
    { value: "without_cash_difference", label: "بدون فرق كاش", requiresAmount: false },
  ],
  return: [
    { value: "without_cash_refund", label: "بدون استرجاع كاش", requiresAmount: false },
  ],
};

/**
 * Build Fincart create-order payload from a Girabatcom enriched order.
 * @see https://fincart.gitbook.io/fincart-api/apis/new-order
 * @param {object} [options]
 * @param {"delivery"|"exchange"|"return"} [options.orderType]
 * @param {string} [options.paymentType]
 * @param {number} [options.amount]
 * @param {number} [options.noOfItems]
 * @param {string} [options.note] appended to description
 * @param {boolean} [options.forceNewReference] unique referenceNumber so Fincart treats it as new
 */
export const buildFincartOrderPayload = (
  order,
  pickupLocationId,
  governorates,
  options = {}
) => {
  const { city, area } = resolveFincartCityArea(order, governorates);
  const phone = String(order.phoneNumber || "").replace(/\D/g, "");
  const backupPhone = String(order.phoneNumber2 || "").replace(/\D/g, "");

  const orderType = options.orderType || "delivery";
  const paymentOptions = FINCART_PAYMENT_OPTIONS[orderType] || FINCART_PAYMENT_OPTIONS.delivery;

  let paymentType = options.paymentType;
  if (!paymentType || !paymentOptions.some((p) => p.value === paymentType)) {
    const defaultAmount = Number(order.total) || 0;
    const wantsCash = defaultAmount > 0;
    paymentType =
      paymentOptions.find((p) => p.requiresAmount === wantsCash)?.value ||
      paymentOptions[0].value;
  }

  const paymentMeta = paymentOptions.find((p) => p.value === paymentType) || paymentOptions[0];
  let amount =
    options.amount !== undefined && options.amount !== null
      ? Number(options.amount)
      : Number(order.total) || 0;

  if (!paymentMeta.requiresAmount) {
    amount = 0;
  } else if (!(amount > 0)) {
    throw new Error("المبلغ لازم يكون أكبر من صفر مع تحصيل الكاش");
  }

  const noOfItems = Math.max(
    Number(options.noOfItems) || order.items?.length || 1,
    1
  );

  const referenceNumber = options.forceNewReference
    ? `Girabatcom.${order.orderNumber}-R${Date.now()}`
    : `Girabatcom.${order.orderNumber}`;

  const locationField =
    orderType === "return"
      ? { returnLocationId: pickupLocationId }
      : { pickupLocationId };

  const openPackageAllowed = orderType === "delivery";

  return {
    instaShip: true,
    source: "s2s",
    orderType,
    customerDetails: {
      name: order.name,
      phone,
      address: {
        addressLine: order.address || "",
        city,
        area,
        landmark: order.apartment || "",
      },
      ...(backupPhone && backupPhone !== phone ? { backupPhone } : {}),
    },
    orderDetails: {
      paymentType,
      ...locationField,
      packageType: "parcel",
      amount,
      noOfItems,
      description: buildDescription(order, options.note),
      openPackageAllowed,
      subBrand: "Girabatcom",
      referenceNumber,
      serviceType: "standard",
    },
  };
};

export const createFincartOrder = async (payload) => {
  const response = await fetch(`${FINCART_BASE_URL}/v4/orders/create`, {
    method: "POST",
    headers: fincartHeaders,
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const errors = Array.isArray(data?.errors) ? data.errors.join(", ") : "";
    throw new Error(errors || data?.message || `فشل إنشاء الطلب (${response.status})`);
  }

  return data;
};

/**
 * Send one Girabat order to Fincart for shipping.
 * Fincart may still create the order but mark orderSubStatus as "duplicate"
 * when the customer/phone already exists in their system.
 * @param {object} [options] passed to buildFincartOrderPayload
 */
export const sendOrderToFincart = async (
  order,
  pickupLocationId,
  governorates,
  options = {}
) => {
  const payload = buildFincartOrderPayload(
    order,
    pickupLocationId,
    governorates,
    options
  );
  const result = await createFincartOrder(payload);
  const data = result?.data || {};
  // POST /v4/orders/create returns the Fincart shipping order id as data.orderId.
  // data.externalId is the courier AWB. data.orderCode is Fincart's own number (GIR-DR-...).
  const fincartOrderId = data.orderId || data.id || null;
  const trackingNumber = data.externalId || null;
  const orderCode = data.orderCode || data.code || null;
  const subStatus = String(data.orderSubStatus || data.subStatus || "").toLowerCase();

  if (!fincartOrderId) {
    console.warn("Fincart create succeeded without a shipping order id", {
      statusCode: result?.statusCode,
      message: result?.message,
      orderSubStatus: subStatus || null,
      responseKeys: data && typeof data === "object" ? Object.keys(data) : [],
    });
  } else {
    console.info("Fincart create returned identifiers", {
      referenceNumber: payload?.orderDetails?.referenceNumber || null,
      fincartOrderId,
      orderCode,
      trackingPresent: !!trackingNumber,
      orderSubStatus: subStatus || null,
    });
  }

  return {
    ...result,
    alreadyInFincart: subStatus === "duplicate",
    referenceNumber: payload?.orderDetails?.referenceNumber,
    fincartOrderId,
    trackingNumber,
    orderCode,
    shipmentPending: !trackingNumber,
  };
};

const FINCART_ID_MAP_KEY = "fincartOrderIdMap";

export const getFincartOrderIdMap = () => {
  try {
    return JSON.parse(localStorage.getItem(FINCART_ID_MAP_KEY) || "{}");
  } catch {
    return {};
  }
};

export const saveFincartOrderId = (girabatOrderId, fincartOrderId) => {
  if (!girabatOrderId || !fincartOrderId) return;
  const map = getFincartOrderIdMap();
  map[String(girabatOrderId)] = fincartOrderId;
  localStorage.setItem(FINCART_ID_MAP_KEY, JSON.stringify(map));
};

export const getStoredFincartOrderId = (girabatOrderId) =>
  getFincartOrderIdMap()[String(girabatOrderId)] || null;

const matchesFincartReference = (refNumber, orderId) => {
  const value = String(refNumber || "").trim();
  const id = String(orderId || "").trim();
  if (!value || !id) return false;
  if (value.toLowerCase() === `girabatcom.${id}`.toLowerCase()) return true;
  if (value === id) return true;
  if (value === `#${id}`) return true;
  if (value.toLowerCase().endsWith(`.${id}`.toLowerCase())) return true;
  return false;
};

export const searchFincartOrders = async (search) => {
  const query = String(search || "").trim();
  if (!query) return [];

  const response = await fetch(`${FINCART_BASE_URL}/v4/orders/`, {
    method: "POST",
    headers: fincartHeaders,
    body: JSON.stringify({ filters: { search: query } }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.message || `فشل البحث في Fincart (${response.status})`);
  }

  return (
    data?.data?.orders ||
    data?.Data?.Orders ||
    data?.data?.Orders ||
    data?.body?.orders ||
    data?.orders ||
    []
  );
};

export const findExistingFincartOrder = async (orderId) => {
  const id = String(orderId || "").trim();
  if (!id) return null;

  const searches = [`Girabatcom.${id}`, id];
  for (const search of searches) {
    const orders = await searchFincartOrders(search);
    const match = (orders || []).find((order) =>
      matchesFincartReference(order.refNumber || order.RefNumber, id)
    );
    if (!match) continue;

    return {
      id: match.id || match.Id || null,
      code: match.code || match.Code || null,
      refNumber: match.refNumber || match.RefNumber || null,
      status: match.status || match.Status || null,
    };
  }

  return null;
};

export const resolveOrderFincartPresence = async (order) => {
  const orderId = order?.orderNumber || order?.orderId;
  const localId =
    order?.shippingOrderId ||
    order?.fincartOrderId ||
    getStoredFincartOrderId(orderId);

  if (order?.sentToFincart || localId) {
    return {
      found: true,
      source: "local",
      fincartOrderId: localId || null,
    };
  }

  const match = await findExistingFincartOrder(orderId);
  if (!match) {
    return { found: false };
  }

  if (match.id) saveFincartOrderId(orderId, match.id);
  return {
    found: true,
    source: "fincart",
    fincartOrderId: match.id,
    code: match.code,
    status: match.status,
  };
};

/**
 * Fetch thermal AWB PDF for one or more Fincart order IDs.
 * @see https://fincart.gitbook.io/fincart-api/apis/print-order-label
 */
export const fetchFincartThermalLabelsPdf = async (fincartOrderIds) => {
  if (!Array.isArray(fincartOrderIds) || fincartOrderIds.length === 0) {
    throw new Error("لا توجد معرفات Fincart للطباعة");
  }

  const response = await fetch(`${FINCART_BASE_URL}/v4/orders/awb`, {
    method: "POST",
    headers: fincartHeaders,
    body: JSON.stringify({
      filters: {
        ids: fincartOrderIds,
        type: "thermal",
      },
    }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const errors = Array.isArray(data?.errors) ? data.errors.join(", ") : "";
    throw new Error(
      errors || data?.message || `فشل جلب بوليصة الشحن (${response.status})`
    );
  }

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    const data = await response.json();
    throw new Error(data?.message || "استجابة غير متوقعة من Fincart عند الطباعة");
  }

  return response.blob();
};

export const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = String(reader.result || "");
      const base64 = result.includes(",") ? result.split(",")[1] : result;
      resolve(base64);
    };
    reader.onerror = () => reject(new Error("فشل تحويل ملف البوليصة"));
    reader.readAsDataURL(blob);
  });
